import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Signature capture.
 *
 * Pointer events rather than touch or mouse events, so a finger, a stylus and a
 * trackpad all work through one code path — the "both contexts" requirement,
 * again without branching.
 *
 * The canvas is backed at device pixel ratio; on a phone a 1x canvas turns a
 * signature into a staircase, and a signature is the one thing on this document
 * that has to look like itself.
 */
export function SignaturePad({
  onCapture,
  onCancel,
  name,
}: {
  onCapture: (blob: Blob) => void
  onCancel: () => void
  name: string
}) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const [hasInk, setHasInk] = useState(false)

  const context = useCallback(() => {
    const element = canvas.current
    if (!element) return null
    const ctx = element.getContext('2d')
    if (!ctx) return null
    ctx.lineWidth = 2.2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    // Stays dark in BOTH themes. The exported PNG is transparent and gets
    // composited onto a white PDF page, so a theme-aware (light) ink would
    // disappear on the printed protocol. The surface adapts instead — `paper`.
    ctx.strokeStyle = '#1a1a1a'
    return ctx
  }, [])

  // Size the backing store to the element's real pixel size.
  useEffect(() => {
    const element = canvas.current
    if (!element) return

    const resize = () => {
      const rect = element.getBoundingClientRect()
      const ratio = window.devicePixelRatio || 1
      element.width = Math.round(rect.width * ratio)
      element.height = Math.round(rect.height * ratio)
      const ctx = element.getContext('2d')
      if (ctx) ctx.scale(ratio, ratio)
    }

    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const positionOf = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.preventDefault()
    // Capture the pointer so a signature that runs off the edge still tracks.
    event.currentTarget.setPointerCapture(event.pointerId)
    const ctx = context()
    if (!ctx) return

    const { x, y } = positionOf(event)
    ctx.beginPath()
    ctx.moveTo(x, y)
    drawing.current = true
    setHasInk(true)
  }

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    event.preventDefault()
    const ctx = context()
    if (!ctx) return

    const { x, y } = positionOf(event)
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const end = () => {
    drawing.current = false
  }

  const clear = () => {
    const element = canvas.current
    const ctx = element?.getContext('2d')
    if (!element || !ctx) return
    ctx.clearRect(0, 0, element.width, element.height)
    setHasInk(false)
  }

  const accept = () => {
    const element = canvas.current
    if (!element || !hasInk) return
    // PNG, because the signature must keep its transparent background: it is
    // drawn over a printed line in the PDF.
    element.toBlob((blob) => blob && onCapture(blob), 'image/png')
  }

  return (
    <div className="border-line bg-surface border p-3">
      <p className="text-muted mb-2 text-xs">
        Unterschrift <span className="text-ink font-medium">{name || '—'}</span>
      </p>

      <canvas
        ref={canvas}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
        // touch-none stops the browser scrolling the page while signing.
        className="border-line bg-paper h-40 w-full touch-none border border-dashed"
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={accept}
          disabled={!hasInk}
          className="bg-brand text-surface hover:bg-brand-strong px-3 py-2 text-xs font-medium transition-colors disabled:opacity-40"
        >
          Unterschrift übernehmen
        </button>
        <button
          type="button"
          onClick={clear}
          className="border-line text-muted hover:text-ink border px-3 py-2 text-xs transition-colors"
        >
          Löschen
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-faint hover:text-ink ml-auto text-xs underline underline-offset-2"
        >
          Abbrechen
        </button>
      </div>
    </div>
  )
}
